/**
 * useShapeQuery — the React binding over the SDK's `watchShape` observable. This
 * is the ONLY place the app couples React to the reactive-read surface: it wraps
 * the observable with `useSyncExternalStore`, so a screen (or the data context)
 * reads a live `ShapeQuery<T>` that re-renders on every broker push — no polling,
 * no bespoke re-query machinery.
 *
 * `watchShape` is an OBSERVABLE (the lib has no React dependency), useQuery-shaped:
 *   { getSnapshot(): ShapeQuery<T>; subscribe(onChange): () => void; refetch() }
 * `getSnapshot` returns a STABLE reference until a real state transition, which is
 * exactly what `useSyncExternalStore` needs to avoid an infinite render loop.
 *
 * The observable is MEMOIZED by (shapeType, scope): re-creating it every render
 * would tear down and re-establish the underlying doc subscriptions on each
 * render. We key the memo on the scope plus the shape's identity (its `@type` /
 * schema-shape) so a stable shapeType yields a stable observable.
 *
 * TIMING + GLOBAL PENDING: on top of the read, this hook instruments EVERY query
 * cycle. When a fresh observable starts waiting for its first result (`isPending`)
 * it registers with the module-level `pendingQueries` store (feeding the global
 * spinner) and stamps `performance.now()`. On the first result (`isPending →
 * success|error`) it resolves the registration and logs the elapsed delay. A new
 * cycle (identity/scope switch recreates the observable) re-registers and
 * re-measures — a query is never "resolved forever". See pendingQueries.ts.
 */

import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { watchShape, type ShapeQuery, type ShapeObservable, type UnionSubject } from '@ng-eventually/polyfill';
import { beginQuery, resolveQuery } from './pendingQueries';
import { recordSet, shapeLabel, totalsSummary, totalSets } from './dataStats';

type Scope = 'public' | 'protected' | 'private';

// Monotonic sequence so several instances of the SAME (shapeType, scope) — e.g.
// two components reading the same shape — get distinct pending ids and never
// collide in the pending Set.
let instanceSeq = 0;

/**
 * Bind a reactive, `useQuery`-shaped read over one SHEX `shapeType` in one logical
 * `scope`. Returns the live `ShapeQuery<T>` (`{ data, isPending, isSuccess,
 * isError, error }`). `data` is ALWAYS an array (never `undefined`); a
 * synced-but-empty scope reads `{ data: [], isPending: false, isSuccess: true }`.
 *
 * The observable is memoized by (shape identity, scope), so it is created ONCE per
 * (shape, scope) and reused across renders — its doc subscriptions are not churned.
 */
export function useShapeQuery<T = UnionSubject>(
  shapeType: unknown,
  scope: Scope,
): ShapeQuery<T> {
  // Derive a stable memo key from the shape's identity. A generated SHEX ShapeType
  // pins its `@type` on `st.shape`; combined with the scope this uniquely keys the
  // observable so a stable shapeType/scope pair reuses one observable.
  const shapeKey =
    (shapeType as { shape?: string } | undefined)?.shape ?? String(shapeType);

  const obs: ShapeObservable<T> = useMemo(
    () => watchShape<T>(shapeType, scope),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeKey, scope],
  );

  const query = useSyncExternalStore(obs.subscribe, obs.getSnapshot);

  // --- TIMING + GLOBAL PENDING (per query CYCLE) ---------------------------
  // A "cycle" = one observable's lifetime. `cycleId` is stable per observable and
  // unique per instance (seq counter), so an identity/scope switch (new observable)
  // yields a NEW cycle id → a fresh begin/measure. Recomputed only when the memo
  // key changes, which is exactly when a new observable is created.
  const cycleId = useMemo(
    () => `${shapeKey}/${scope}#${++instanceSeq}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeKey, scope],
  );

  // Track whether THIS cycle has already resolved, so we log/resolve exactly once
  // and stay symmetric under StrictMode's double-invoked effects.
  const startedAtRef = useRef<number | null>(null);
  const resolvedRef = useRef(false);

  // Begin the cycle: mark pending + stamp start time. Keyed on cycleId so a new
  // observable re-begins. Cleanup resolves if we unmount (or the cycle changes)
  // while still pending — otherwise the spinner would hang. Idempotent store ops
  // make the StrictMode double-run harmless.
  useEffect(() => {
    startedAtRef.current = performance.now();
    resolvedRef.current = false;
    beginQuery(cycleId);
    return () => {
      resolveQuery(cycleId);
    };
  }, [cycleId]);

  // On the first result of this cycle (isPending → success|error), resolve the
  // registration once and log the delay. Guarded by resolvedRef so the double
  // effect run under StrictMode logs a single line per real transition.
  useEffect(() => {
    if (resolvedRef.current) return;
    if (query.isPending) return;
    resolvedRef.current = true;
    const startedAt = startedAtRef.current;
    const elapsed = startedAt == null ? 0 : Math.round(performance.now() - startedAt);
    resolveQuery(cycleId);
    const n = Array.isArray(query.data) ? query.data.length : 0;
    // A readable set-reception log: HOW MANY objects of WHICH shape, in which
    // scope, and how long the first result took. `shapeKey` is the shape URI
    // (…/Event); `shapeLabel` trims it to the readable shape name (Event).
    const label = shapeLabel(shapeKey);
    // Cumulative tally across the session (per shape), so the raw broker
    // "readDoc → N rows" logs gain an app-level running total.
    recordSet(label, n);
    // eslint-disable-next-line no-console
    // No identity in scope at this call site (this hook receives no currentUserId) —
    // identity-first prefix falls back to the bare `[app][data]` form.
    console.log(`[app][data] set reçu: ${n} objets ${label} (${scope}) en ${elapsed}ms`);
    // eslint-disable-next-line no-console
    console.log(`[app][data] totaux — ${totalsSummary()} (${totalSets()} sets reçus)`);
  }, [query, cycleId, shapeKey, scope]);

  return query;
}
